@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.engineservice.cr

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.dashboard.DashboardNode
import org.example.project.engineservice.ServiceApi
import org.example.project.enginetabular.currentDateDE
import org.example.project.manager.NodeScope
import org.example.project.service.CRChatPopupService
import org.example.project.service.LoginService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

private const val CUS_CR_PDF_EXPORT_ROOT_ID = "cus-cr-pdf-export-root"

private fun resolveCustomerOrt(selectedCr: All?, preview: CrPreviewLayout?): String {
    val previewOrt = preview?.empfaengerPlzOrt
        ?.trim()
        .orEmpty()
        .split(Regex("\\s+"), limit = 2)
        .getOrNull(1)
        .orEmpty()
    return previewOrt.ifBlank { selectedCr?.ort.orEmpty() }
}

@Composable
fun CUSCRBearbeiten(
    verlaufCrs: List<All>
) {
    val scope = rememberCoroutineScope()
    var verlaufFilter by remember { mutableStateOf("ALLE") }
    var selectedCrId by remember { mutableStateOf<String?>(null) }
    var showPdfPreview by remember { mutableStateOf(false) }
    var selectedPreviewLayout by remember { mutableStateOf<CrPreviewLayout?>(null) }
    var accepting by remember { mutableStateOf(false) }
    var confirmingReadyForDeployment by remember { mutableStateOf(false) }
    var currentVerlaufCrs by remember(verlaufCrs) { mutableStateOf(verlaufCrs) }

    LaunchedEffect(verlaufCrs) {
        currentVerlaufCrs = verlaufCrs
    }

    val currentUser = remember(currentVerlaufCrs.size, showPdfPreview) { LoginService.getUser() ?: All() }
    val signaturName = listOf(currentUser.vorname, currentUser.nachname)
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .ifBlank { currentUser.personalnummer }
        .ifBlank { currentUser.id }
    val signaturRolle = currentUser.position
    val selectedCr = currentVerlaufCrs.firstOrNull { it.id == selectedCrId }
    val kundenFirma = selectedPreviewLayout?.empfaengerFirmenname
        ?.takeIf { it.isNotBlank() }
        ?: selectedCr?.firma
        .orEmpty()
    val kundenOrt = resolveCustomerOrt(selectedCr, selectedPreviewLayout)
    val kundenOrtDatum = listOf(
        "Angebot angenommen",
        listOf(kundenOrt, currentDateDE()).filter { it.isNotBlank() }.joinToString(", ")
    ).filter { it.isNotBlank() }.joinToString("\n")

    CUSCRBearbeitenLayout(
        verlaufCrs = currentVerlaufCrs,
        verlaufFilter = verlaufFilter,
        selectedCrId = selectedCrId,
        showPdfPreview = showPdfPreview,
        selectedPreviewLayout = selectedPreviewLayout,
        previewOrtDatumText = kundenOrtDatum,
        previewSignaturName = signaturName,
        previewSignaturRole = signaturRolle,
        previewFirmenName = kundenFirma,
        previewSignaturBild = currentUser.signatur,
        onFilterChange = { verlaufFilter = it },
        onOpenCr = { cr ->
            if (cr.zustand == org.example.project.service.ZustandManager.CR.EINGESPIELT) {
                scope.launch {
                    val fileName = cr.crAngebotPdf.ifBlank { ServiceApi.crPdfFileName(cr) }
                    ServiceApi.openCrDocument(cr.id, fileName)
                }
            } else {
                selectedCrId = cr.id
                showPdfPreview = false
                selectedPreviewLayout = null
            }
        },
        onSelectCr = {
            selectedCrId = it
            if (it == null) {
                showPdfPreview = false
                selectedPreviewLayout = null
            }
        },
        onOpenDocument = { cr ->
            selectedCrId = cr.id
            scope.launch {
                if (cr.zustand >= org.example.project.service.ZustandManager.CR.VERBINDLICH) {
                    val fileName = cr.crAngebotPdf.ifBlank { ServiceApi.crPdfFileName(cr) }
                    ServiceApi.openCrDocument(cr.id, fileName)
                } else {
                    showPdfPreview = true
                }
            }
        },
        onOpenChat = { cr ->
            CRChatPopupService.openPopup(NodeScope.CUS, DashboardNode.CUS, cr)
        },
        accepting = accepting,
        onAcceptOffer = { cr ->
            if (accepting) return@CUSCRBearbeitenLayout
            accepting = true
            scope.launch {
                try {
                    val preview = selectedPreviewLayout ?: ServiceApi.loadCrPreview(cr.id)
                    if (preview == null) {
                        AppDialogs.alert("Angebot konnte nicht angenommen werden, weil keine Angebotsvorschau vorhanden ist.")
                        return@launch
                    }
                    selectedPreviewLayout = preview
                    delay(75)
                    val exportRoot = document.getElementById(CUS_CR_PDF_EXPORT_ROOT_ID) as? HTMLElement
                    if (exportRoot == null) {
                        AppDialogs.alert("Angebot konnte nicht angenommen werden, weil das CR-Dokument nicht gerendert werden konnte.")
                        return@launch
                    }
                    val fileName = cr.crAngebotPdf.ifBlank { ServiceApi.crPdfFileName(cr) }
                    val pdfFile = renderLetterDocumentPdfFile(exportRoot, fileName)
                    val pdfUrl = ServiceApi.uploadServiceDocument("cr", cr.id, pdfFile, fileName)
                    if (pdfUrl.isBlank()) {
                        AppDialogs.alert("Angebot konnte nicht angenommen werden, weil das finale CR-PDF nicht gespeichert werden konnte.")
                        return@launch
                    }
                    val updated = ServiceApi.acceptCrOffer(cr.id)
                    if (updated != null) {
                        currentVerlaufCrs = currentVerlaufCrs.map { existing ->
                            if (existing.id == updated.id) updated else existing
                        }
                        showPdfPreview = false
                    }
                } finally {
                    accepting = false
                }
            }
        },
        confirmingReadyForDeployment = confirmingReadyForDeployment,
        onConfirmReadyForDeployment = { cr ->
            if (confirmingReadyForDeployment) return@CUSCRBearbeitenLayout
            confirmingReadyForDeployment = true
            scope.launch {
                try {
                    val updated = ServiceApi.transitionCrState(cr.id, org.example.project.service.ZustandManager.CR.BEREIT_ZUR_EINSPIELUNG)
                    if (updated != null) {
                        currentVerlaufCrs = currentVerlaufCrs.map { existing ->
                            if (existing.id == updated.id) updated else existing
                        }
                    }
                } finally {
                    confirmingReadyForDeployment = false
                }
            }
        },
        onShowPdfPreviewChange = { show ->
            showPdfPreview = show
        }
    )

    LaunchedEffect(selectedCrId, showPdfPreview) {
        if (selectedCr != null && (showPdfPreview || selectedPreviewLayout == null)) {
            selectedPreviewLayout = ServiceApi.loadCrPreview(selectedCr.id)
        } else {
            selectedPreviewLayout = null
        }
    }

    if (selectedCr != null && selectedPreviewLayout != null) {
        Div({
            style {
                position(Position.Fixed)
                left((-10000).px)
                top(0.px)
                width(794.px)
                property("pointer-events", "none")
                opacity(0)
                property("z-index", "-1")
            }
        }) {
            CUSCRPreviewDocument(
                selectedPreviewLayout = selectedPreviewLayout,
                previewOrtDatumText = kundenOrtDatum,
                previewSignaturName = signaturName,
                previewSignaturRole = signaturRolle,
                previewFirmenName = kundenFirma,
                previewSignaturBild = currentUser.signatur,
                rootElementId = CUS_CR_PDF_EXPORT_ROOT_ID
            )
        }
    }
}
